import type { ExplicitAddress } from '@cronos-app/sdk';
import type { Token } from '@ui/config/token';
import { useTokenBalance } from '@ui/hooks/contracts/useTokenBalance';
import { useTokenConfig } from '@ui/hooks/contracts/useTokenConfig';
import { useMaxValue } from '@ui/hooks/useMaxValue';
import {
  validateMaxDecimals,
  validateMaxValue,
  validateMinValue,
  validateNotEmpty,
  validateNotZero,
  validateNumberFormat,
} from '@ui/utils/validations';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

interface UseAmountInputErrorParams {
  fromToken: Token;
  tokenAddress: ExplicitAddress;
  enteredAmountStr: string;
}

export const useAmountInputError = ({
  fromToken,
  tokenAddress,
  enteredAmountStr,
}: UseAmountInputErrorParams) => {
  const t = useTranslations();
  const fromTokenInfo = useTokenConfig({
    token: fromToken,
    tokenAddress: tokenAddress,
  });

  const maxDecimals = fromTokenInfo?.decimals;

  const balance = useTokenBalance({
    tokenAddress: fromTokenInfo?.address,
  }).data;

  const maxValue = useMaxValue(fromToken, balance);

  return useMemo(() => {
    return (
      validateNotEmpty({
        t,
        value: enteredAmountStr,
      }) ||
      validateNumberFormat({
        t,
        value: enteredAmountStr,
      }) ||
      validateNotZero({
        t,
        value: enteredAmountStr,
      }) ||
      validateMinValue({
        t,
        value: enteredAmountStr,
        minValue: 0,
      }) ||
      validateMaxValue({
        t,
        value: enteredAmountStr,
        maxValue,
      }) ||
      (typeof maxDecimals === 'number' &&
        validateMaxDecimals({
          t,
          value: enteredAmountStr,
          maxDecimals: maxDecimals,
        }))
    );
  }, [t, enteredAmountStr, maxValue, maxDecimals]);
};
